import { useCallback } from 'react';
import useGetProfileImage from '@/hooks/get/useGetProfileImage';
import useGetCustomerPersonalDetails from '@/hooks/get/useGetCustomerPersonalDetails';
import useInstance from '@/hooks/custom-hooks/useInstance';

export interface CompanyDisplayResult {
  logoUrl: string | undefined;
  companyName: string | undefined;
  homeCompanyName: string | undefined;
  isLoading: boolean;
  isSwitched: boolean;
  onGoHome: () => void;
}

interface UseCompanyDisplayOptions {
  handleCustomerRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  companyDetails?: { companyName?: string | null } | null;
}

/**
 * Encapsulates company logo, name, and "go home" logic for the navbar.
 * Replaces the legacy NavLogo component's data-fetching responsibilities.
 *
 * Must be rendered inside `<AuthGate>` — no auth guard needed.
 */
export function useCompanyDisplay({
  handleCustomerRequest,
  companyDetails,
}: UseCompanyDisplayOptions): CompanyDisplayResult {
  const { data: profileImage, isLoading: isImageLoading, status } =
    useGetProfileImage(handleCustomerRequest);
  const { data: personalDetails } = useGetCustomerPersonalDetails(handleCustomerRequest);
  const { onKillInstance, isSwitched } = useInstance({});

  const logoUrl = profileImage?.content
    ? `data:image/png;base64,${profileImage.content}`
    : undefined;

  const onGoHome = useCallback(() => {
    onKillInstance();
  }, [onKillInstance]);

  return {
    logoUrl: status === 'success' ? logoUrl : undefined,
    companyName: companyDetails?.companyName ?? undefined,
    homeCompanyName: personalDetails?.companyName ?? undefined,
    isLoading: isImageLoading || status !== 'success',
    isSwitched: Boolean(isSwitched()),
    onGoHome,
  };
}
